import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Select, Checkbox, RadioGroup } from '~/components/Form';
import { CUSTOMIZACAO_RAIZEN } from '~/constants/modules';
import { t, can } from '~/helpers';
import { storeData, updateData } from '~/store/modules/perfilFrentista/actions';

import { Container } from './styles';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();
  const { client } = useSelector((state) => state.user.profile);
  const {
    formadigitacaofrota,
    leitortagfrentista,
    habilitanivelfrentistaterminalclienteconfig,
  } = useSelector((state) => state.user.configs);
  const { loading, find, params } = useSelector(
    (state) => state.perfilFrentista
  );
  const initials = create ? data : { ...find, ...data };

  const optionParamEight = useMemo(
    () => [
      { label: t('s.cadastro.usuario.acessoterminal.semacesso'), value: 0 },
      {
        label: t('s.cadastro.usuario.acessoterminal.nivelfrentista'),
        value: 1,
      },
      { label: t('s.cadastro.usuario.acessoterminal.nivellider'), value: 2 },
      {
        label: t('s.cadastro.usuario.acessoterminal.niveladministrador'),
        value: 3,
      },
      {
        label: t(
          's.cadastro.usuario.acessoterminal.niveladministrador.nivelmestre'
        ),
        value: 4,
      },
    ],
    []
  );

  const optionsParam7 = useMemo(
    () =>
      Number(formadigitacaofrota) === 1
        ? [
            {
              label: t('s.cadastro.usuario.liberacaoabastecimento.naolibera'),
              value: '0',
            },
            {
              label: t(
                's.cadastro.usuario.liberacaoabastecimento.comtranspondercadastrado'
              ),
              value: '1',
            },
            {
              label: t(
                's.cadastro.usuario.liberacaoabastecimento.liberadigitacao'
              ),
              value: '4',
            },
            {
              label: t(
                's.cadastro.usuario.liberacaoabastecimento.liberadigitacaocadastro'
              ),
              value: '5',
            },
          ]
        : [
            {
              label: t('s.cadastro.usuario.liberacaoabastecimento.naolibera'),
              value: '0',
            },
            {
              label: t(
                's.cadastro.usuario.liberacaoabastecimento.comtranspondercadastrado'
              ),
              value: '1',
            },
            {
              label: t(
                's.cadastro.usuario.liberacaoabastecimento.comtranspondernaocadastrado'
              ),
              value: '2',
            },
            {
              label: t(
                's.cadastro.usuario.liberacaoabastecimento.semtransponder'
              ),
              value: '3',
            },
          ],
    [formadigitacaofrota]
  );

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        nomeperfilfrentista: Yup.string()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(1, t('s.geral.validacao.campoobrigatorio'))
          .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50)),
        param5perfilfrentista: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        param7perfilfrentista: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        param8perfilfrentista:
          can(CUSTOMIZACAO_RAIZEN) ||
          habilitanivelfrentistaterminalclienteconfig == 'S'
            ? Yup.number()
                .typeError(t('s.geral.validacao.campoobrigatorio'))
                .required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
        // can(CUSTOMIZACAO_RAIZEN) && ['F', 'A'].includes(typeUser)
        //   ? Yup.number()
        //     .typeError(t('s.geral.validacao.campoobrigatorio'))
        //     .required(t('s.geral.validacao.campoobrigatorio'))
        //   : Yup.string().nullable(),
        statusperfilfrentista: Yup.string()
          .nullable()
          .required('s.geral.validacao.campoobrigatorio'),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        param1perfilfrentista: 0,
        param2perfilfrentista: 0,
        param3perfilfrentista: 0,
        param4perfilfrentista: 0,
        param5perfilfrentista: 0,
        param6perfilfrentista: 0,
        param7perfilfrentista: 0,
        param8perfilfrentista: 0,
        param9perfilfrentista: 0,
        param10perfilfrentista: 0,
        ...initials,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeData(objSend, params));
      } else {
        await dispatch(updateData(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="nomeperfilfrentista"
            label={t('s.cadastro.usuario.perfilfrentista.nomeperfilfrentista')}
          />

          <ExpansionPanelSummary
            aria-controls="panel2a-content"
            id="panel2a-header"
            style={{
              background: 'rgba(0, 0, 0, 0.05)',
              borderBottom: '1px solid rgba(0,0,0,.2)',
            }}
          >
            <Typography>{t('s.cadastro.usuario.operacao')}</Typography>
          </ExpansionPanelSummary>

          <ExpansionPanelDetails>
            <Container>
              <Typography style={{ marginBottom: '1rem' }}>
                {t('s.cadastro.usuario.operacoesterminal')}
              </Typography>

              <Checkbox
                name="param1perfilfrentista"
                label={t('s.cadastro.usuario.liberaafericao')}
                // disabled={typeUser === 'U'}
                serialize={(value) => Number(value)}
              />

              <Checkbox
                name="param2perfilfrentista"
                label={t('s.cadastro.usuario.recebecombustivel')}
                // disabled={typeUser === 'U'}
                serialize={(value) => Number(value)}
              />

              <Checkbox
                name="param3perfilfrentista"
                label={t('s.cadastro.usuario.acertaestoque')}
                // disabled={typeUser === 'U'}
                serialize={(value) => Number(value)}
              />

              <Checkbox
                name="param4perfilfrentista"
                label={t('s.cadastro.usuario.acertaencerrante')}
                // disabled={typeUser === 'U'}
                serialize={(value) => Number(value)}
              />

              <Checkbox
                name="param10perfilfrentista"
                label={t('s.cadastro.usuario.calibracao')}
                serialize={(value) => Number(value)}
              />

              <Checkbox
                name="param6perfilfrentista"
                label={t('s.cadastro.usuario.associatransponder')}
                // disabled={typeUser === 'U'}
                style={{ marginBottom: '3rem' }}
                serialize={(value) => Number(value)}
              />

              {
                (habilitanivelfrentistaterminalclienteconfig == 'S' ||
                  can(CUSTOMIZACAO_RAIZEN)) && (
                  // ['F', 'A'].includes(typeUser) && (
                  <Select
                    name="param8perfilfrentista"
                    label={t('s.cadastro.usuario.acessoterminal')}
                    options={optionParamEight}
                  />
                )
                // )
              }

              <RadioGroup
                name="param5perfilfrentista"
                label={t('s.cadastro.usuario.trocamodooperacional')}
                options={[
                  {
                    label: t(
                      's.cadastro.usuario.trocamodooperacional.naolibera'
                    ),
                    value: '0',
                  },
                  {
                    label: t(
                      's.cadastro.usuario.trocamodooperacional.liberamanualintermitente'
                    ),
                    value: '1',
                  },
                  {
                    label: t(
                      's.cadastro.usuario.trocamodooperacional.liberamanualconstante'
                    ),
                    value: '2',
                  },
                ]}
                // disabled={typeUser === 'U'}
                serialize={(value) => Number(value)}
              />

              <RadioGroup
                name="param7perfilfrentista"
                label={t('s.cadastro.usuario.liberacaoAbastecimento')}
                options={optionsParam7}
                // disabled={typeUser === 'U'}
                serialize={(value) => Number(value)}
              />
            </Container>
          </ExpansionPanelDetails>

          <Select
            name="statusperfilfrentista"
            label={t('s.cadastro..usuario.perfil.statusperfil')}
            options={[
              { value: 'L', label: t('s.geral.opcoes.liberado') },
              { value: 'B', label: t('s.geral.opcoes.bloqueado') },
              { value: 'C', label: t('s.geral.opcoes.cancelado') },
            ]}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
